import { Component, OnDestroy, OnInit } from "@angular/core";
import { User } from "@core/models";
import { Subject, Subscription, interval, takeUntil } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import {
  AnonPaymentStatusResponse,
  AnonymousContestPaymentDTO,
  Contest,
  ContestMemberDTO,
  GroupMember,
} from "../../models/cahoot";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { CahootService } from "../../cahoot.service";
import { ToastrService } from "ngx-toastr";
import { AuthenticationService } from "@core/services/auth";
import { CoreConfigService } from "@core/services/config.service";
import { SignUpModalComponent } from "../sign-up-modal/sign-up-modal.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { SchoolService } from "@core/services/school.service";
import { PaymentService } from "@core/services/payment.service";
import { BeewizPlan } from "../select-plan-modal/select-plan-modal.component";
import { serverErrorMessage } from "@core/helpers";

type ContestStep = 'entry' | 'checking' | 'payment' | 'polling';

@Component({
  standalone: false,
  selector: "app-join-cahoot-non-logged-in-users",
  templateUrl: "./join-cahoot-non-logged-in-users.component.html",
  styleUrls: ["./join-cahoot-non-logged-in-users.component.scss"],
})
export class JoinCahootNonLoggedInUsersComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<boolean>();
  private schoolSearch$ = new Subject<string>();
  private pollSub: Subscription | null = null;

  // Contest state
  contestId: number;
  form: number;
  currentUser: User;
  title: string;
  contestCreatorId: number;
  queryParams: Contest = {};
  isContestCreator: boolean;
  activeSessions: Contest[];
  joinContestForm: FormGroup;
  paymentForm: FormGroup;
  isLoadingContest = true;
  isTeacher: boolean;
  contestCode: string;
  difficultLevel: string;
  subject: string;
  topic: string;
  contest: Contest;
  isGettingContestDetails = true;
  errorMessage: string;
  tokenId: string;
  showErrorModal: boolean = false;
  submitted: boolean = false;
  urlExtension: string;
  groupMember: GroupMember;
  wantToJoinContest: boolean = false;

  // BeeWiz anonymous flow
  isAnonFlow = false;
  step: ContestStep = 'entry';
  sessionId: string;
  selectedSchool: { id: number; name: string } | null = null;
  schoolSearchQuery = '';
  schoolResults: any[] = [];
  beewizPlan: BeewizPlan | null = null;
  showSchoolDropdown = false;
  isSearchingSchools = false;
  anonStatus: AnonPaymentStatusResponse | null = null;
  pollCount = 0;
  readonly maxPolls = 90;
  paymentRetries = 0;
  readonly maxRetries = 3;

  // Subject selection on payment screen
  readonly availableSubjects = ['Mathematics', 'English', 'Geography', 'Biology'];
  selectedSubjects = new Set<string>();

  toggleSubject(subject: string): void {
    if (this.selectedSubjects.has(subject)) {
      this.selectedSubjects.delete(subject);
    } else {
      this.selectedSubjects.add(subject);
    }
  }

  constructor(
    private router: Router,
    private cahootService: CahootService,
    private toast: ToastrService,
    private authService: AuthenticationService,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private coreConfigService: CoreConfigService,
    private authenticationService: AuthenticationService,
    private modal: NgbModal,
    private schoolService: SchoolService,
    private paymentService: PaymentService,
  ) {}

  ngOnInit(): void {
    this.contestCode = this.activatedRoute.snapshot.queryParams.contestCode;
    this.tokenId = this.activatedRoute.snapshot.queryParams.tokenId;
    this.currentUser = this.authService.currentUserValue;
    this.urlExtension = this.activatedRoute.snapshot.queryParams.ext;
    this.wantToJoinContest = this.activatedRoute.snapshot.queryParams.wantToJoinContest;

    if (this.tokenId) {
      this.getLoginFromTokenId(this.tokenId);
      return;
    }

    if (!this.currentUser?.userId) {
      this.configureLayout();
      localStorage.removeItem("currentUser");
      this.isAnonFlow = true;
    }

    if (this.urlExtension) this.getStudyGroupMemberDetails();

    this.initjoinContestForm();
    this.initPaymentForm();
    this.sessionId = localStorage.getItem('anonSessionId') ?? this.generateUUID();
    localStorage.setItem('anonSessionId', this.sessionId);
    this.setupSchoolSearch();
    this.getContestByCode();
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private setupSchoolSearch(): void {
    this.schoolSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$),
    ).subscribe((query) => {
      if (!query || query.length < 2) {
        this.schoolResults = [];
        return;
      }
      this.isSearchingSchools = true;
      this.schoolService.search(query)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (res) => {
            this.schoolResults = Array.isArray(res) ? res : (res?.content || []);
            this.isSearchingSchools = false;
          },
          error: () => {
            this.schoolResults = [];
            this.isSearchingSchools = false;
          },
        });
    });
  }

  onSchoolSearch(value: string): void {
    this.schoolSearchQuery = value;
    this.selectedSchool = null;
    this.showSchoolDropdown = true;
    this.schoolSearch$.next(value);
  }

  selectSchool(school: any): void {
    this.selectedSchool = { id: school.id, name: school.name };
    this.schoolSearchQuery = school.name;
    this.showSchoolDropdown = false;
    this.schoolResults = [];
  }

  cantFindSchool(): void {
    this.selectedSchool = { id: 0, name: this.schoolSearchQuery || 'Unknown School' };
    this.showSchoolDropdown = false;
    this.schoolResults = [];
  }

  hideDropdownDelayed(): void {
    setTimeout(() => { this.showSchoolDropdown = false; }, 200);
  }

  proceedToContest(): void {
    this.submitted = true;
    const displayName = this.joinContestForm.get('displayName').value?.trim();
    if (!displayName || !this.selectedSchool) return;

    this.step = 'checking';
    this.cahootService.checkAnonPaymentStatus(this.sessionId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.anonStatus = res;
          if (!res.paymentRequired || res.hasSubscription) {
            this.doJoinAnonContest();
          } else {
            this.loadBeewizPlan();
          }
        },
        error: () => {
          this.loadBeewizPlan();
        },
      });
  }

  private loadBeewizPlan(): void {
    this.paymentService.getPlansAnon()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (plans: BeewizPlan[]) => {
          this.beewizPlan = plans.find(p => p.planType === 'BEEWIZ') ?? null;
          if (this.beewizPlan && !this.anonStatus?.amount) {
            if (!this.anonStatus) this.anonStatus = {};
            this.anonStatus.amount = this.beewizPlan.discountedAmount || this.beewizPlan.amount;
          }
          // Pre-select the contest's subject in the subject picker
          if (this.contest?.subject) {
            const match = this.availableSubjects.find(
              s => s.toLowerCase() === this.contest.subject.toLowerCase()
            );
            if (match) this.selectedSubjects.add(match);
          }
          this.step = 'payment';
        },
        error: () => {
          this.step = 'payment';
        },
      });
  }

  makePayment(): void {
    if (this.paymentForm.invalid) return;

    const phone = this.paymentForm.value.phone as string;
    const payload: AnonymousContestPaymentDTO = {
      sessionId: this.sessionId,
      contestId: this.contest.contestId,
      phone,
      planId: this.beewizPlan?.planId ?? 1,
      status: null,
    };

    this.step = 'checking';
    this.cahootService.initiateAnonPayment(payload)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.step = 'polling';
          this.startPolling();
        },
        error: (err) => {
          this.toast.error(serverErrorMessage(err, "Couldn't start the payment. Please try again."));
          this.step = 'payment';
        },
      });
  }

  private startPolling(): void {
    this.pollCount = 0;
    this.pollSub = interval(2000)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.pollCount++;
        if (this.pollCount > this.maxPolls) {
          this.pollSub?.unsubscribe();
          if (this.paymentRetries < this.maxRetries) {
            this.paymentRetries++;
            this.toast.warning('Payment timed out. Please try again.');
          } else {
            this.toast.error('Payment could not be completed. Please try again later.');
          }
          this.step = 'payment';
          return;
        }

        this.cahootService.checkAnonPaymentStatus(this.sessionId)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (res) => {
              if (res.status === 'PAID' || res.paymentStatus === 'CONFIRMED' || (!res.paymentRequired && res.hasSubscription)) {
                this.pollSub?.unsubscribe();
                this.anonStatus = res;
                this.doJoinAnonContest();
              } else if (res.status === 'FAILED' || res.paymentStatus === 'FAILED') {
                this.pollSub?.unsubscribe();
                if (this.paymentRetries < this.maxRetries) {
                  this.paymentRetries++;
                  this.toast.warning(res.message || 'Payment failed. Please try again.');
                } else {
                  this.toast.error('Payment could not be completed. Please try again later.');
                }
                this.step = 'payment';
              }
            },
            error: () => {},
          });
      });
  }

  private doJoinAnonContest(): void {
    this.step = 'checking';
    const payload: ContestMemberDTO = {
      contestId: this.contest.contestId,
      displayName: this.joinContestForm.get('displayName').value,
      schoolId: this.selectedSchool.id,
      schoolName: this.selectedSchool.name,
      sessionId: this.sessionId,
      userId: null,
      memberId: null,
      token: null,
      contestStatus: 0,
      points: 0,
      correctAnswers: 0,
      wrongAnswers: 0,
    };

    this.cahootService.joinAnonContest(payload)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          localStorage.setItem('memberId', res.memberId);
          localStorage.setItem('userName', res.displayName);
          if (res.token) {
            const tok: any = { access_token: res.token };
            this.authenticationService.setTokens(tok).subscribe(() => {
              this.navigateToWaitingRoom();
            });
            return;
          }
          this.navigateToWaitingRoom();
        },
        error: (err) => {
          this.toast.error(serverErrorMessage(err, "Couldn't join this contest. Please try again."));
          this.step = 'entry';
        },
      });
  }

  private navigateToWaitingRoom(): void {
    this.router.navigate(['/beewiz/waiting-room'], {
      queryParams: {
        contestId: this.contest.contestId,
        title: this.contest.title,
        createdByUserId: this.contest.createdByUserId,
        isContestCreator: false,
      },
    });
  }

  backToEntry(): void {
    this.step = 'entry';
    this.submitted = false;
  }

  navigateBack(): void {
    this.router.navigate(['/login']);
  }

  // ── Existing methods (kept) ──────────────────────────────────────────────

  getLoginFromTokenId(tokenId: string): void {
    this.authService
      .loginWithTokenId(tokenId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => this.afterTokenLogin(),
        error: () => {
          this.isGettingContestDetails = false;
          this.configureLayout();
          this.router.navigate(["/login"]);
          this.toast.error("Sorry, an error occurred");
        },
      });
  }

  private afterTokenLogin(): void {
    this.currentUser = this.authService.currentUserValue;

    if (this.currentUser) {
      this.initjoinContestForm();
      this.getContestByCode();
    } else {
      window.location.reload();
    }
  }

  configureLayout() {
    this.coreConfigService.config = {
      layout: {
        navbar: { hidden: true },
        footer: { hidden: true },
        menu: { hidden: true },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  initjoinContestForm(): void {
    this.joinContestForm = this.fb.group({
      displayName: ["", Validators.required],
    });
  }

  initPaymentForm(): void {
    this.paymentForm = this.fb.group({
      phone: ['', [Validators.required, Validators.pattern(/^254[0-9]{9}$/)]],
    });
  }

  getContestByCode(): void {
    if (!this.contestCode) return;
    this.cahootService
      .getContestByCode(this.contestCode)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.isGettingContestDetails = false;
          if (res.contestId) {
            this.contest = res;
            if (this.contest.tryoutId && !this.wantToJoinContest) return this.navigateToCounterPage();
            this.queryParams = {
              contestId: this.contest.contestId,
              title: this.contest.title,
              createdByUserId: this.contest.createdByUserId,
              isContestCreator: this.currentUser?.userId == this.contest.createdByUserId,
            };
            this.patchNameField();
          }
        },
        error: (err) => {
          this.isGettingContestDetails = false;
          this.errorMessage = err.error.message
            ? err.error.message
            : "Sorry, this contest does not exist. Please check with the creator";
          if (!this.currentUser) {
            this.showErrorModal = true;
            this.launchSignUpModal(this.errorMessage);
          }
        },
      });
  }

  navigateToCounterPage(): void {
    const queryParams = {
      contestTime: this.contest.startDate,
      tryoutId: this.contest.tryoutId,
      subjectName: this.contest.subject,
      contestCode: this.contest.code
    };
    this.router.navigate(["/tryout/count-down"], { queryParams });
  }

  getStudyGroupMemberDetails(): void {
    this.cahootService
      .getGroupMemberByUrlExtension(this.urlExtension)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.groupMember = res;
          this.queryParams = {
            ...this.queryParams,
            ...(this.groupMember ? { invitedStudyGroupId: this.groupMember.groupId } : {}),
          };
        },
        error: () => {},
      });
  }

  joinContest(): void {
    this.submitted = true;
    if (!this.joinContestForm.get("displayName").value) return;
    const displayName = this.currentUser?.userId
      ? this.currentUser.name
      : this.joinContestForm.get("displayName").value;
    const payload = {
      userId: this.currentUser?.userId ?? null,
      displayName: displayName,
      contestId: this.contest.contestId,
      ...(this.groupMember ? { groupMemberId: this.groupMember.id } : {}),
    };

    this.cahootService
      .joinContest(payload)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          const memberId = res.memberId;
          localStorage.setItem("memberId", memberId);
          localStorage.setItem("userName", res.displayName);

          if (res.token) {
            let tok: any = {};
            tok.access_token = res.token;
            this.authenticationService.setTokens(tok).subscribe(() => {
              this.router.navigate(["/beewiz/waiting-room"], { queryParams: this.queryParams });
            });
            return;
          }
          this.router.navigate(["/beewiz/waiting-room"], { queryParams: this.queryParams });
        },
        error: (err) => {
          this.toast.error(
            (this.errorMessage = serverErrorMessage(err, "Couldn't join this contest. Please try again."))
          );
        },
      });
  }

  get f() {
    return this.joinContestForm.controls;
  }

  patchNameField(): void {
    if (this.currentUser?.userId || this.groupMember) {
      this.joinContestForm
        .get("displayName")
        .patchValue(this.groupMember ? this.groupMember.name : this.currentUser.name);
      this.joinContestForm.get("displayName").disable();
    }
    this.joinContestForm.updateValueAndValidity();
  }

  launchSignUpModal(errorMessage?: string): void {
    const checkAnsModal = this.modal.open(SignUpModalComponent, {
      centered: true,
      backdrop: "static",
      animation: true,
    });
    checkAnsModal.componentInstance.errorMessage = errorMessage;
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
    this.unsubscribe$.next(true);
  }
}
